import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { SUPPORTED_CURRENCIES } from '../utils/fx';
import { Icon } from '../tokens';
import Dropzone from '../components/Dropzone';
import './Upload.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const EMPTY_FORM = {
  name: '', launchYear: '', sku: '', brand: '', scale: '', action: '', category: '',
  priceAmount: '', priceCurrency: 'USD', priceNegotiable: false,
  material: '', description: '', units: '1',
};

const BRANDS = [
  'Auto Art', 'Auto World', 'BBR', 'Kaido House', 'Burago', 'Corgi', 'Colección Fasciculo', 'CMC', 'D Model', 'Franklin Mint',
  'Greenlight Collectibles', 'Hobby Japan', 'Hot Wheels', 'Inno Models', 'Jada Toys', 'Kyosho', 'Maisto',
  'Majorette', 'Matchbox', 'Mini GT', 'M2 Machines', 'Norev', 'QubeCarz', 'Revell', 'Schuco',
  'Solido', 'Tamiya', 'Tarmac', 'Time Micro', 'Tomica', 'Top Speed', 'Welly', 'Pop Race', 'Ignition Model', 'Shelby', 'Trends Hobby', 'Other',
];
const CATEGORIES = [
  'American Muscle', 'Classic Muscle', 'Concept Cars', 'Custom Cars', 'Dragsters',
  'Electric Cars', 'Exotics', 'Formula 1', 'Hot Rods', 'IndyCar', 'JDM',
  'Movie & TV Cars', 'NASCAR', 'Off-Road', 'Police & Fire', 'Race Cars',
  'Rally Cars', 'Supercars', 'Trucks & SUVs', 'Vintage Cars', 'Other',
];
const MATERIALS = [
  'Aluminum', 'Ceramic', 'Die-cast metal', 'Fiberglass', 'Paper',
  'Plastic', 'Resin', 'Rubber', 'Vinyl', 'Wood',
];
const SCALES = ['1/18', '1/24', '1/32', '1/43', '1/64', '1/87'];
const ACTIONS = ['Mostrar', 'Venta', 'Cambio'];

function Field({ label, children }) {
  return (
    <div className="uf">
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}

export default function Upload() {
  const { user, refreshUser, listCollections, createCollection, getPublication, deletePublication } = useAuth();
  const { displayCurrency } = useCurrency();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  // ?action=Venta preselecciona la acción (lo usa el «Subir pieza en venta»
  // del panel de vendedor). El botón + del navbar entra sin acción.
  const [searchParams] = useSearchParams();

  // Solo Premium/Seller/Admin pueden poner piezas en venta o cambio. El plan
  // gratuito (role 'user') publica únicamente en modo 'Mostrar'.
  const canSellOrTrade = ['user_premium', 'seller', 'admin'].includes(user?.role);
  const [upgradeNotice, setUpgradeNotice] = useState(false);

  const [files, setFiles] = useState([]); // archivos comprimidos en cliente, listos para subir
  const [existingImages, setExistingImages] = useState([]); // URLs ya guardadas en S3
  const [removeImages, setRemoveImages] = useState([]);     // URLs marcadas para borrar
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(isEdit);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState(() => {
    const preset = searchParams.get('action');
    const action = !isEdit && canSellOrTrade && ACTIONS.includes(preset) ? preset : '';
    return { ...EMPTY_FORM, action, priceCurrency: displayCurrency || 'USD' };
  });

  // Collections (carpetas) — selección en el momento de publicar.
  const [collections, setCollections] = useState([]);
  const [selectedColls, setSelectedColls] = useState(new Set());
  const [newCollName, setNewCollName] = useState('');
  const [creatingColl, setCreatingColl] = useState(false);
  const [collErr, setCollErr] = useState('');

  useEffect(() => {
    if (!user) return;
    listCollections().then(setCollections).catch(() => setCollections([]));
  }, [user, listCollections]);

  // Pre-cargar la publicación cuando estamos editando.
  useEffect(() => {
    if (!isEdit || !user) return;
    let cancelled = false;
    setBootstrapping(true);
    getPublication(editId)
      .then((pub) => {
        if (cancelled) return;
        if (!pub || (pub.user?._id || pub.user) !== user._id) {
          setError(t('upload.err_not_owner', { defaultValue: 'No puedes editar esta pieza.' }));
          return;
        }
        setForm({
          name:            pub.name        || '',
          launchYear:      pub.launchYear  != null ? String(pub.launchYear) : '',
          sku:             pub.sku         || '',
          brand:           pub.brand       || '',
          scale:           pub.scale       || '',
          action:          pub.action      || '',
          category:        pub.category    || '',
          priceAmount:     pub.price?.amount != null ? String(pub.price.amount) : '',
          priceCurrency:   pub.price?.currency || displayCurrency || 'USD',
          priceNegotiable: !!pub.price?.negotiable,
          material:        pub.material    || '',
          description:     pub.description || '',
          units:           pub.units != null ? String(pub.units) : '1',
        });
        setExistingImages(pub.images || []);
        setSelectedColls(new Set((pub.collections || []).map(String)));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.message || t('upload.err_load', { defaultValue: 'No se pudo cargar la pieza.' }));
      })
      .finally(() => { if (!cancelled) setBootstrapping(false); });
    return () => { cancelled = true; };
  }, [isEdit, editId, user, getPublication, t, displayCurrency]);

  const toggleColl = (id) => setSelectedColls((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleCreateColl = async () => {
    const name = newCollName.trim();
    if (!name) return;
    setCreatingColl(true); setCollErr('');
    try {
      const c = await createCollection({ name });
      setCollections((prev) => [...prev, c]);
      setSelectedColls((prev) => new Set(prev).add(c._id));
      setNewCollName('');
    } catch (err) {
      setCollErr(err.response?.data?.message || 'Error');
    } finally { setCreatingColl(false); }
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const removeExisting = (url) => {
    setRemoveImages((prev) => prev.includes(url) ? prev : [...prev, url]);
  };

  const handleSubmit = async (status = 'active') => {
    if (!user) return navigate('/login');
    setError(''); setLoading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('images', f));
      // Excluimos los campos planos del precio: el backend los recompone.
      const PRICE_FIELDS = new Set(['priceAmount', 'priceCurrency', 'priceNegotiable']);
      Object.entries(form).forEach(([k, v]) => {
        if (PRICE_FIELDS.has(k)) return;
        if (v !== '' && v != null) fd.append(k, v);
      });
      // Empaquetar price como JSON si hay amount; multipart/form-data no soporta
      // objetos anidados nativamente.
      if (form.priceAmount !== '' && form.priceAmount != null) {
        fd.append('price', JSON.stringify({
          amount: Number(form.priceAmount),
          currency: form.priceCurrency || 'USD',
          negotiable: !!form.priceNegotiable,
        }));
      }
      fd.append('status', status);
      if (selectedColls.size > 0) fd.append('collections', JSON.stringify([...selectedColls]));

      let data;
      if (isEdit) {
        if (removeImages.length) fd.append('removeImages', JSON.stringify(removeImages));
        ({ data } = await axios.patch(`${API}/api/publications/${editId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }));
      } else {
        ({ data } = await axios.post(`${API}/api/publications`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }));
      }
      // Un Premium que pone su primera pieza en venta queda con la solicitud
      // de vendedor en curso: refrescamos su sesión y el perfil lo avisa.
      if (data?.sellerRequest === 'created') {
        await refreshUser();
        navigate('/profile', { state: { sellerRequested: true } });
        return;
      }
      navigate('/profile');
    } catch (err) { setError(err.response?.data?.message || t('upload.err_publish')); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm(t('upload.delete_confirm', { defaultValue: '¿Eliminar esta pieza? Esta acción no se puede deshacer.' }))) return;
    setError(''); setDeleting(true);
    try {
      await deletePublication(editId);
      navigate('/profile');
    } catch (err) {
      setError(err.response?.data?.message || t('upload.err_delete', { defaultValue: 'No se pudo eliminar.' }));
    } finally { setDeleting(false); }
  };

  const TIPS = [
    { icon: Icon.camera(12), text: t('upload.tip1') },
    { icon: Icon.doc(12),    text: t('upload.tip2') },
    { icon: Icon.verified(12), text: t('upload.tip3') },
  ];

  return (
    <div className="upload-page">
      <div className="upload-body container">
        <div className="upload-main">
          <h1 className="upload-h1">
            {isEdit
              ? t('upload.title_edit', { defaultValue: 'Editar pieza' })
              : t('upload.title')}
          </h1>
          <p className="upload-sub">
            {isEdit
              ? t('upload.subtitle_edit', { defaultValue: 'Actualiza los datos, agrega o quita fotos.' })
              : t('upload.subtitle')}
          </p>

          {error && <div className="error-banner">{error}</div>}
          {bootstrapping && (
            <p style={{ color: 'var(--muted)', fontSize: 13, margin: '8px 0' }}>
              {t('upload.loading', { defaultValue: 'Cargando pieza…' })}
            </p>
          )}

          {/* ── Dropzone (compresión client-side <300KB) ── */}
          <Dropzone
            newFiles={files}
            setNewFiles={setFiles}
            existingImages={existingImages}
            removedExisting={removeImages}
            onRemoveExisting={removeExisting}
            maxFiles={6}
          />

          {/* ── Fields ── */}
          <div className="upload-fields">
            <div className="frow frow--3">
              <Field label={t('upload.name_label')}><input className="light-input" placeholder={t('upload.name_placeholder')} value={form.name} onChange={set('name')} required /></Field>
              <Field label={t('upload.year_label')}><input className="light-input" type="number" placeholder={t('upload.year_placeholder')} value={form.launchYear} onChange={set('launchYear')} /></Field>
              <Field label={t('upload.sku_label')}><input className="light-input" placeholder={t('upload.sku_placeholder')} value={form.sku} onChange={set('sku')} /></Field>
            </div>
            <div className="frow frow--3">
              <Field label={t('upload.brand_label')}>
                <select className="light-select" value={form.brand} onChange={set('brand')} required>
                  <option value="" disabled>{t('upload.select')}</option>
                  {BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <Field label={t('upload.category_label')}>
                <select className="light-select" value={form.category} onChange={set('category')} required>
                  <option value="" disabled>{t('upload.select')}</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label={t('upload.material_label')}>
                <select className="light-select" value={form.material} onChange={set('material')}>
                  <option value="" disabled>{t('upload.select')}</option>
                  {MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
            </div>
            <div className="frow frow--3">
              <Field label={t('upload.scale_label')}>
                <select className="light-select" value={form.scale} onChange={set('scale')}>
                  <option value="" disabled>{t('upload.select')}</option>
                  {SCALES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={t('upload.action_label')}>
                <select
                  className="light-select"
                  value={form.action}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!canSellOrTrade && value !== 'Mostrar') {
                      setUpgradeNotice(true);
                      setForm((f) => ({ ...f, action: 'Mostrar' }));
                      return;
                    }
                    setUpgradeNotice(false);
                    setForm((f) => ({ ...f, action: value }));
                  }}
                  required
                >
                  <option value="" disabled>{t('upload.select')}</option>
                  {ACTIONS.map((a) => <option key={a} value={a}>{t(`upload.action_options.${a}`, { defaultValue: a })}</option>)}
                </select>
                {upgradeNotice && (
                  <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    🔒 {t('upload.action_upgrade_notice')}{' '}
                    <Link to="/precios" style={{ color: 'var(--teal)', fontWeight: 600 }}>
                      {t('upload.action_upgrade_link')}
                    </Link>
                  </p>
                )}
              </Field>
              <Field label={t('upload.price_amount', { defaultValue: 'Precio' })}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <div className="price-wrap" style={{ flex: 1 }}>
                    <span className="price-sym">$</span>
                    <input
                      className="light-input"
                      type="number"
                      placeholder="0.00"
                      value={form.priceAmount}
                      onChange={set('priceAmount')}
                      style={{ paddingLeft: 26 }}
                    />
                  </div>
                  <select
                    className="light-select"
                    value={form.priceCurrency}
                    onChange={set('priceCurrency')}
                    aria-label={t('upload.price_currency', { defaultValue: 'Divisa' })}
                    style={{ width: 90 }}
                  >
                    {SUPPORTED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
                  <input
                    type="checkbox"
                    checked={!!form.priceNegotiable}
                    onChange={(e) => setForm((f) => ({ ...f, priceNegotiable: e.target.checked }))}
                  />
                  {t('upload.negotiable', { defaultValue: 'Negociable' })}
                </label>
              </Field>
            </div>
            {(user?.role === 'seller' || user?.role === 'admin') && (
              <div className="frow frow--3">
                <Field label={t('upload.units_label', { defaultValue: 'Unidades disponibles' })}>
                  <input
                    className="light-input"
                    type="number"
                    min="1"
                    placeholder="1"
                    value={form.units}
                    onChange={set('units')}
                  />
                  <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                    {t('upload.units_hint', { defaultValue: 'Cantidad de unidades de la misma pieza que tienes en inventario.' })}
                  </p>
                </Field>
              </div>
            )}
            <Field label={t('upload.desc_label')}>
              <textarea className="light-input" style={{ height: 'auto', minHeight: 100, resize: 'vertical', lineHeight: 1.6 }} placeholder={t('upload.desc_placeholder')} value={form.description} onChange={set('description')} />
            </Field>

            {/* ── Carpetas ── */}
            <div className="uf upload-collections">
              <label className="field-label">
                {Icon.folder(12)} {t('upload.collections_label', { defaultValue: 'Carpetas' })}
              </label>
              <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: -2, marginBottom: 8 }}>
                {t('upload.collections_hint', { defaultValue: 'Opcional. Marca las carpetas donde quieres que aparezca esta pieza.' })}
              </p>
              {collections.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {collections.map((c) => {
                    const active = selectedColls.has(c._id);
                    return (
                      <button
                        key={c._id}
                        type="button"
                        className={`chip${active ? ' chip--active' : ''}`}
                        onClick={() => toggleColl(c._id)}
                      >
                        {Icon.folder(12)} {c.name}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
                  {t('upload.collections_empty', { defaultValue: 'Aún no tienes carpetas. Crea una abajo.' })}
                </p>
              )}
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  className="light-input"
                  style={{ flex: 1, fontSize: 13 }}
                  placeholder={t('upload.collections_new_placeholder', { defaultValue: 'Ej. JDM Clásicos' })}
                  value={newCollName}
                  onChange={(e) => setNewCollName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateColl(); } }}
                  maxLength={60}
                />
                <button
                  type="button"
                  className="btn-ghost"
                  style={{ padding: '0 14px', fontSize: 12 }}
                  onClick={handleCreateColl}
                  disabled={creatingColl || !newCollName.trim()}
                >
                  {creatingColl ? '…' : t('upload.collections_new_btn', { defaultValue: '+ Crear carpeta' })}
                </button>
              </div>
              {collErr && <p style={{ fontSize: 11, color: '#ef4444', marginTop: 6 }}>{collErr}</p>}
            </div>
          </div>

          <div className="upload-actions" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-teal" style={{ padding: '12px 24px' }} onClick={() => handleSubmit('active')} disabled={loading || deleting || bootstrapping}>
                {loading
                  ? (isEdit ? t('upload.saving', { defaultValue: 'Guardando…' }) : t('upload.publishing'))
                  : (isEdit ? t('upload.save_btn', { defaultValue: 'Guardar cambios' }) : t('upload.publish_btn'))}
              </button>
              <button className="btn-ghost" style={{ padding: '12px 24px' }} onClick={() => handleSubmit('draft')} disabled={loading || deleting || bootstrapping}>
                {t('upload.draft_btn')}
              </button>
            </div>
            {isEdit && (
              <button
                type="button"
                className="btn-ghost"
                style={{ padding: '12px 20px', color: '#ef4444' }}
                onClick={handleDelete}
                disabled={deleting || loading || bootstrapping}
              >
                {deleting
                  ? t('upload.deleting', { defaultValue: 'Eliminando…' })
                  : t('upload.delete_btn', { defaultValue: 'Eliminar pieza' })}
              </button>
            )}
          </div>
        </div>

        {/* ── Tips ── */}
        <aside className="upload-tips">
          <p className="tips-title">{t('upload.tips_title')}</p>
          {TIPS.map((tip, i) => (
            <div key={i} className="tip-row">
              <div className="tip-icon">{tip.icon}</div>
              <p className="tip-text">{tip.text}</p>
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}
